import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTranslation } from 'react-i18next';

import { disableModalDiagramExportSQL } from '../../../redux/diagram/diagram.actions';
import { selectDiagramDetails } from '../../../redux/main/main.selectors';

import { selectElements, selectLines, selectAreas, selectIsModalDiagramExportSqlEnabled, selectModalDiagramExportSqlData } from '../../../redux/diagram/diagram.selectors';

import { showNotify } from '../../../redux/notify/notify.actions';

import { copyToClipboard } from '../../../utils/copy-to-clipboard';

import { trackEvent } from '../../../lib/analytics';

import Modal from '../Modal/Modal';
import './ModalDiagramExportSQL.scss';

import getMySqlCode from '../../../utils/export-sql-code/mysql';
import getPostgreSqlCode from '../../../utils/export-sql-code/postgresql';
import getOracleCode from '../../../utils/export-sql-code/oracle';
import getSqlServerCode from '../../../utils/export-sql-code/sqlserver';
import getMariaDbCode from '../../../utils/export-sql-code/mariadb';
import getSqliteCode from '../../../utils/export-sql-code/sqlite';


function buildDbEngineName(dbEngine = '') {
  switch (dbEngine) {
    case 'basicsql':
      return 'Basic SQL';
    case 'mysql':
      return 'MySQL';
    case 'postgresql':
      return 'PostgreSQL';
    case 'sqlserver':
      return 'SQL Server';
    case 'oracle':
      return 'Oracle';
    case 'sqlite':
      return 'SQLite';
    case 'mariadb':
      return 'MariaDB';
    default:
      return 'General SQL';
  }
}

const sqlParsers = {
  mysql: (tables, projectName) => {
    return getMySqlCode(tables, projectName);
  },
  postgresql: (tables, projectName) => {
    return getPostgreSqlCode(tables, projectName);
  },
  oracle: (tables, projectName) => {
    return getOracleCode(tables, projectName);
  },
  sqlserver: (tables, projectName) => {
    return getSqlServerCode(tables, projectName);
  },
  mariadb: (tables, projectName) => {
    return getMariaDbCode(tables, projectName);
  },
  sqlite: (tables, projectName) => {
    return getSqliteCode(tables, projectName);
  },
};

function projectNameToInCodeName(projectName) {
  return projectName
    .trim()                           // Видаляє пробіли на початку та в кінці
  // .replace(/[^a-z0-9_]/g, '');    // Видаляє всі символи, які не є літерами, цифрами або підкресленнями
}
function projectNameToFileName(projectName) {
  return projectName
    .trim()                           // Видаляє пробіли на початку та в кінці
    .toLowerCase()                    // Перетворює всі літери на малі
    .replaceAll("&", "and")
    .replace(/\s+/g, '_')             // Замінює пробіли на нижні підкреслення
    .replace(/[^a-z0-9_]/g, '');      // Видаляє всі символи, які не є літерами, цифрами або підкресленнями
}

const generateCode = (dbEngine, diagramDetails, elements, lines) => {
  const projectNameInCode = projectNameToInCodeName(diagramDetails?.title || '');
  const projectName = projectNameToFileName(diagramDetails?.title || '');

  const code = sqlParsers[dbEngine](elements, projectNameInCode);

  // const projectTag = '1.45'; // diagramDetails?.tag
  // const dbEngine = 'postgresql';
  // const dbVersion = '14';

  // [project_name].[tag.1.45].[postgresql_v.14].dbdesign
  // const fileName = `${projectName}.[tag.${projectTag}].[${dbEngine}_v.${dbVersion}].dbdesign.sql`;

  // const fileName = `${projectName}.[${dbEngine}].dbdesign.sql`;
  const fileName = `${projectName}.${dbEngine}.sql`;
  return [code, fileName]
};

const downloadFile = (content, fileName) => {
  const blob = new Blob([content], { type: 'text/sql' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(link.href);
};

export const ModalDiagramExportSQL = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const isOpen = useSelector(selectIsModalDiagramExportSqlEnabled);
  const modalData = useSelector(selectModalDiagramExportSqlData);

  const diagramDetails = useSelector(selectDiagramDetails);
  const elements = useSelector(selectElements);
  const lines = useSelector(selectLines);
  const areas = useSelector(selectAreas);

  const textareaRef = useRef(null);

  const [dbEngineName, setDbEngineName] = useState('');
  const [value, setValue] = useState('');
  const [fileName, setFileName] = useState('');

  useEffect(() => {
    if (!modalData?.dbEngine) return;

    const [code, codeFileName] = generateCode(modalData?.dbEngine, diagramDetails, elements, lines);

    setDbEngineName(buildDbEngineName(modalData?.dbEngine));
    setValue(code);
    setFileName(codeFileName);

    return () => { };
  }, [modalData]);

  const handleCancel = () => {
    dispatch(disableModalDiagramExportSQL());
    setTimeout(() => {
      setDbEngineName('');
      setValue('');
      setFileName('');
    }, 300);
  };

  const handleDownloadFile = async (e) => {
    try {
      trackEvent('playground_modal_export_code_download_file', {
        page: 'playground',
        type: 'modals',
      });
      setTimeout(() => { dispatch(showNotify({ message: 'file_ready_to_download' })) }, 100);
      setTimeout(() => { downloadFile(value, fileName) }, 300);
    } catch (err) { }
  }

  // const handleCopyCode = async (e) => {
  //   try {
  //     await navigator.clipboard.writeText(value);
  //     dispatch(showNotify({ message: 'code_copied' }));
  //   } catch (err) { }
  // }

  const handleCopyCode = async (e) => {
    e?.preventDefault?.();
    trackEvent('playground_modal_export_code_copy_link', {
      page: 'playground',
      type: 'modals',
    });
    const ok = await copyToClipboard(value);
    if (ok) {
      dispatch(showNotify({ message: 'code_copied' }));
    } else {
      // dispatch(showNotify({ message: 'code_copy_failed' }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      modalClass={`modal-diagram-export-sql`}
      onCancel={handleCancel}
    >
      <div className="modal-content-header">{t('m_export_code.title', { db_engine: dbEngineName || 'General SQL' })}</div>
      <div className="modal-content-body">
        <textarea
          // disabled
          readOnly
          maxLength={1000}
          ref={textareaRef}
          autoComplete="off"
          autoFocus={(window.innerWidth >= 1000)}
          spellCheck="false"
          value={value}
        />
        <div className='copy-button' onClick={() => handleCopyCode()}>
          <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: '1px' }}>
            <path d="M8 8V5.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C9.52 2 10.08 2 11.2 2h7.6c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C22 3.52 22 4.08 22 5.2v7.6c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C20.48 16 19.92 16 18.8 16H16M5.2 22h7.6c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874C16 20.48 16 19.92 16 18.8v-7.6c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C14.48 8 13.92 8 12.8 8H5.2c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C2 9.52 2 10.08 2 11.2v7.6c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C3.52 22 4.08 22 5.2 22"></path>
          </svg>
        </div>

        <div className='buttons-section'>
          <button className='button-second' onClick={() => handleDownloadFile()}>{t('m_export_code.download_file')}</button>
          <button onClick={() => handleCopyCode()}>{t('m_export_code.copy_code')}</button>
        </div>
      </div>
    </Modal>
  );
};

export default ModalDiagramExportSQL;
